import * as React from "react";
import { ArrowUpDown, ChevronDown } from "lucide-react";
import {
  useListSortContext,
  useTranslate,
} from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const ContactSortButton = () => {
  const { sort, setSort } = useListSortContext();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  const fieldLabels: Record<string, string> = {
    first_name: translate("simplecrm.contacts.first_name", { _: "Primeiro nome" }),
    last_name: translate("simplecrm.contacts.last_name", { _: "Sobrenome" }),
    last_seen: translate("ra.resources.contacts.fields.last_seen", { _: "Último visto" }),
  };

  // Gerar todas as combinações de campo + ordem
  const sortOptions = [
    { field: "first_name", order: "ASC" as const },
    { field: "first_name", order: "DESC" as const },
    { field: "last_name", order: "ASC" as const },
    { field: "last_name", order: "DESC" as const },
    { field: "last_seen", order: "ASC" as const },
    { field: "last_seen", order: "DESC" as const },
  ];

  const handleChangeSort = (field: string, order: "ASC" | "DESC") => {
    setSort({ field, order });
    setOpen(false);
  };

  const fieldLabel = fieldLabels[sort.field] || sort.field;
  const buttonLabel = translate("ra.sort.sort_by", {
    field: fieldLabel,
    field_lower_first:
      typeof fieldLabel === "string"
        ? fieldLabel.charAt(0).toLowerCase() + fieldLabel.slice(1)
        : undefined,
    order: translate(`ra.sort.${sort.order}`),
    _: `Sort by ${sort.field} ${sort.order}`,
  });

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {isMobile ? (
        <TooltipProvider>
          <Tooltip>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={buttonLabel}
                >
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent>
              <p>{buttonLabel}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9">
            <ArrowUpDown className="h-4 w-4" />
            <span className="ml-2">{buttonLabel}</span>
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="start">
        {sortOptions.map((option) => (
          <DropdownMenuItem 
            key={`${option.field}-${option.order}`} 
            onClick={() => handleChangeSort(option.field, option.order)}
          >
            {fieldLabels[option.field]}{" "}
            {translate(`ra.sort.${option.order}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const inverseOrder = (sort: string) => (sort === "ASC" ? "DESC" : "ASC");

